import { motion } from "framer-motion";
import {
  Anchor,
  Ship,
  Compass,
  Stamp,
  Building2,
  FileCheck2,
  ArrowUpRight,
} from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const SERVICES = [
  {
    chapter: "01",
    title: "Custom House Agency",
    subtitle: "CHA · Port & Border Clearance",
    desc: "Bill of entry filing, tariff classification, port & LCS clearance, assessments and dispute resolution for ocean, air and land-border cargo.",
    tags: ["BOE Filing", "Tariff Classification", "LCS Birpara"],
    icon: Anchor,
    testid: "service-card-cha",
    wide: true,
  },
  {
    chapter: "02",
    title: "Freight Forwarding",
    subtitle: "Sea · Air · Multimodal",
    desc: "FCL/LCL container booking, cargo insurance, chartering and end-to-end execution across international trade lanes.",
    tags: ["FCL / LCL", "Cargo Insurance"],
    icon: Ship,
    testid: "service-card-freight",
    wide: false,
  },
  {
    chapter: "03",
    title: "Importer–Exporter Consultancy",
    subtitle: "Foreign Trade Policy Advisory",
    desc: "Duty drawback claims, advance authorisation, RoDTEP, EPCG and SEZ/EOU compliance strategy for active traders.",
    tags: ["RoDTEP", "EPCG", "Drawback"],
    icon: Compass,
    testid: "service-card-consultancy",
    wide: false,
  },
  {
    chapter: "04",
    title: "Trademark Filing & IP",
    subtitle: "Brand Protection Agency",
    desc: "Trademark search, online filing, objection replies, renewals and IP strategy so your brand stays yours.",
    tags: ["TM Search", "Objection Reply"],
    icon: Stamp,
    testid: "service-card-trademark",
    wide: false,
  },
  {
    chapter: "05",
    title: "Incorporation & Compliances",
    subtitle: "Company Formation · MCA",
    desc: "Pvt Ltd, LLP and partnership registration, DIN/DSC, annual MCA filings and corporate governance support.",
    tags: ["Pvt Ltd / LLP", "MCA Filings"],
    icon: Building2,
    testid: "service-card-incorporation",
    wide: false,
  },
  {
    chapter: "06",
    title: "GST & IEC Registration",
    subtitle: "Tax & Trade Licensing",
    desc: "Import Export Code generation, GST registration for cross-border businesses, LUT filing and monthly return assistance.",
    tags: ["IEC in 48H", "LUT Filing", "GST Returns"],
    icon: FileCheck2,
    testid: "service-card-iec",
    wide: true,
  },
];

export default function Services({ onEnquire }) {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 sm:py-32 bg-[#0A0E17]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-16"
        >
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-amber-400 mb-4">
            The Manifesto — Six Chapters
          </p>
          <h2 className="font-display font-bold tracking-tight uppercase text-2xl sm:text-3xl lg:text-5xl text-slate-100 max-w-3xl leading-tight">
            Every document. Every duty.{" "}
            <span className="text-[#F59E0B]">Every deadline.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.chapter}
              data-testid={s.testid}
              initial={{ opacity: 0, y: 48 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.12, ease: EASE }}
              className={`group relative bg-[#161F33] border border-white/[0.08] rounded-md p-6 sm:p-8 flex flex-col overflow-hidden hover:border-[#F59E0B]/40 transition-colors duration-500 ${
                s.wide ? "md:col-span-2" : ""
              }`}
            >
              <span className="absolute -top-4 right-4 font-display font-extrabold text-[90px] leading-none text-white/[0.04] group-hover:text-[#F59E0B]/10 transition-colors duration-500 select-none">
                {s.chapter}
              </span>
              <div className="flex items-center gap-4 mb-6">
                <span className="w-11 h-11 grid place-items-center border border-white/10 rounded-sm text-[#F59E0B] group-hover:bg-[#F59E0B] group-hover:text-[#0A0E17] transition-colors duration-500">
                  <s.icon size={20} strokeWidth={1.75} />
                </span>
                <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
                  Chapter {s.chapter}
                </span>
              </div>
              <h3 className="font-display font-bold text-xl sm:text-2xl text-white tracking-tight uppercase">
                {s.title}
              </h3>
              <p className="mt-1 font-mono2 text-[11px] uppercase tracking-[0.2em] text-amber-400/80">
                {s.subtitle}
              </p>
              <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed flex-1">
                {s.desc}
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {s.tags.map((t) => (
                  <span
                    key={t}
                    className="font-mono2 text-[10px] uppercase tracking-wider text-slate-400 border border-white/10 rounded-full px-3 py-1"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <button
                data-testid={`${s.testid}-enquire`}
                onClick={() => onEnquire(s.title)}
                className="mt-7 self-start flex items-center gap-2 font-mono2 text-xs uppercase tracking-[0.2em] text-slate-300 hover:text-[#F59E0B] transition-colors duration-300"
              >
                Enquire
                <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </button>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
